/**
 * Currency formatting utilities for TIATaxi
 * Supports Albanian Lek (ALL) and Euro (EUR)
 */

/**
 * Format a number as Albanian Lek
 * @param {number} amount - The amount to format
 * @param {boolean} showSymbol - Whether to show the L symbol (default: true)
 * @returns {string} Formatted currency string (e.g., "1,234.00 L")
 */
export const formatLek = (amount, showSymbol = true) => {
  if (amount === null || amount === undefined || isNaN(amount)) {
    return showSymbol ? '0.00 L' : '0.00';
  }
  
  const formatted = Number(amount).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
  
  return showSymbol ? `${formatted} L` : formatted;
};

/**
 * Format a number as Euro
 * @param {number} amount - The amount to format
 * @param {boolean} showSymbol - Whether to show the € symbol (default: true)
 * @returns {string} Formatted currency string (e.g., "€1,234.00")
 */
export const formatEuro = (amount, showSymbol = true) => {
  if (amount === null || amount === undefined || isNaN(amount)) {
    return showSymbol ? '€0.00' : '0.00';
  }
  
  const formatted = Number(amount).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
  
  return showSymbol ? `€${formatted}` : formatted;
};

/**
 * Convert Lek to Euro
 * @param {number} lekAmount - Amount in Lek
 * @param {number} exchangeRate - Exchange rate (default: 100 Lek per Euro)
 * @returns {number} Amount in Euro
 */
export const lekToEuro = (lekAmount, exchangeRate = 100) => {
  if (!lekAmount || isNaN(lekAmount) || !exchangeRate) return 0;
  return lekAmount / exchangeRate;
};

/**
 * Convert Euro to Lek
 * @param {number} euroAmount - Amount in Euro
 * @param {number} exchangeRate - Exchange rate (default: 100 Lek per Euro)
 * @returns {number} Amount in Lek
 */
export const euroToLek = (euroAmount, exchangeRate = 100) => {
  if (!euroAmount || isNaN(euroAmount)) return 0;
  return euroAmount * exchangeRate;
};

/**
 * Format amount with both Lek and Euro
 * @param {number} lekAmount - Amount in Lek
 * @param {number} exchangeRate - Exchange rate
 * @returns {object} Object with both formatted values
 */
export const formatBothCurrencies = (lekAmount, exchangeRate = 100) => {
  const euroAmount = lekToEuro(lekAmount, exchangeRate);
  return {
    lek: formatLek(lekAmount),
    euro: formatEuro(euroAmount),
    lekRaw: lekAmount,
    euroRaw: euroAmount
  };
};

/**
 * Format a number with thousand separators
 * @param {number} num - The number to format
 * @param {number} decimals - Number of decimal places (default: 0)
 * @returns {string} Formatted number string
 */
export const formatNumber = (num, decimals = 0) => {
  if (num === null || num === undefined || isNaN(num)) {
    return decimals > 0 ? '0.' + '0'.repeat(decimals) : '0';
  }
  
  return Number(num).toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  });
};

export default {
  formatLek,
  formatEuro,
  lekToEuro,
  euroToLek,
  formatBothCurrencies,
  formatNumber
};
